import {
  useEffect,
  useLayoutEffect,
  useId,
  useRef,
  useState,
  type SelectHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import './unified-select.css';
type Option = { value: string; label: string; disabled: boolean };
export function UnifiedSelect({
  children,
  className = '',
  id: suppliedId,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  const generatedId = useId(),
    id = suppliedId || generatedId;
  const native = useRef<HTMLSelectElement>(null),
    trigger = useRef<HTMLButtonElement>(null),
    panel = useRef<HTMLDivElement>(null);
  const [options, setOptions] = useState<Option[]>([]),
    [open, setOpen] = useState(false),
    [position, setPosition] = useState({ left: 0, top: 0, width: 200, maxHeight: 300 });
  const [selected, setSelected] = useState('');
  useLayoutEffect(() => {
    const el = native.current!;
    setOptions(
      Array.from(el.options).map((o) => ({ value: o.value, label: o.text, disabled: o.disabled })),
    );
    setSelected(el.value);
  }, [children, props.value, props.defaultValue]);
  const close = () => {
    setOpen(false);
    trigger.current?.focus({ preventScroll: true });
  };
  function fitPopup(desiredHeight: number) {
    const r = trigger.current!.getBoundingClientRect();
    const visual = window.visualViewport;
    const dialog = trigger.current!.closest('dialog')?.getBoundingClientRect();
    const leftEdge = Math.max(visual?.offsetLeft || 0, dialog?.left || 0) + 8;
    const rightEdge =
      Math.min(
        (visual?.offsetLeft || 0) + (visual?.width || document.documentElement.clientWidth),
        document.documentElement.clientWidth,
        dialog?.right ?? Infinity,
      ) - 8;
    const topEdge = Math.max(visual?.offsetTop || 0, dialog?.top || 0) + 8;
    const bottomEdge =
      Math.min(
        (visual?.offsetTop || 0) + (visual?.height || innerHeight),
        dialog?.bottom ?? Infinity,
      ) - 8;
    const width = Math.max(0, Math.min(Math.max(r.width, 200), rightEdge - leftEdge));
    const below = Math.max(0, bottomEdge - r.bottom - 6),
      above = Math.max(0, r.top - topEdge - 6);
    const downward = below >= desiredHeight || below >= above;
    const maxHeight = Math.min(300, downward ? below : above);
    return {
      left: Math.max(leftEdge, Math.min(r.left, rightEdge - width)),
      top: downward
        ? r.bottom + 6
        : Math.max(topEdge, r.top - Math.min(desiredHeight, maxHeight) - 6),
      width,
      maxHeight,
    };
  }
  function show() {
    setPosition(fitPopup(Math.min(300, options.length * 44 + 12)));
    setOpen(true);
  }
  useLayoutEffect(() => {
    if (!open || !panel.current) return;
    // Wrapped labels can be taller than the estimated single-line rows.
    const height = Math.min(300, panel.current.scrollHeight + 2);
    setPosition(fitPopup(height));
  }, [open, options]);
  useEffect(() => {
    if (!open) return;
    panel.current
      ?.querySelector<HTMLButtonElement>('[aria-selected="true"]:not(:disabled)')
      ?.focus({ preventScroll: true });
    const outside = (e: PointerEvent) => {
      if (
        !panel.current?.contains(e.target as Node) &&
        !trigger.current?.contains(e.target as Node)
      )
        setOpen(false);
    };
    const dismiss = (e: Event) => {
      if (!panel.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    window.visualViewport?.addEventListener('resize', dismiss);
    window.visualViewport?.addEventListener('scroll', dismiss);
    return () => {
      document.removeEventListener('pointerdown', outside);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('scroll', dismiss, true);
      window.visualViewport?.removeEventListener('resize', dismiss);
      window.visualViewport?.removeEventListener('scroll', dismiss);
    };
  }, [open]);
  function choose(option: Option) {
    const el = native.current!;
    el.value = option.value;
    setSelected(option.value);
    el.dispatchEvent(new Event('change', { bubbles: true }));
    close();
  }
  return (
    <>
      <select
        {...props}
        ref={native}
        id={id + '-native'}
        className="unified-select-native"
        tabIndex={-1}
        aria-hidden="true"
        onInvalid={(e) => {
          e.preventDefault();
          trigger.current?.focus();
          show();
        }}
      >
        {children}
      </select>
      <button
        ref={trigger}
        type="button"
        id={id}
        className={'unified-select ' + className}
        disabled={props.disabled}
        title={props.title}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
        aria-describedby={props['aria-describedby']}
        aria-required={props.required}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? id + '-list' : undefined}
        onClick={() => (open ? close() : show())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            show();
          }
        }}
      >
        <span>{options.find((o) => o.value === selected)?.label || '请选择'}</span>
        <ChevronDown size={14} />
      </button>
      {open &&
        createPortal(
          <div
            ref={panel}
            id={id + '-list'}
            role="listbox"
            aria-label={props['aria-label'] || '选择选项'}
            className="unified-select-menu"
            style={position}
            onKeyDown={(e) => {
              const enabled = Array.from(
                panel.current!.querySelectorAll<HTMLButtonElement>(
                  '[role="option"]:not(:disabled)',
                ),
              );
              const index = enabled.indexOf(document.activeElement as HTMLButtonElement);
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                close();
              } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
                e.preventDefault();
                enabled[
                  e.key === 'Home'
                    ? 0
                    : e.key === 'End'
                      ? enabled.length - 1
                      : (index + (e.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length
                ]?.focus();
              } else if (e.key === 'Tab') {
                setOpen(false);
                trigger.current?.focus();
              } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
                enabled
                  .find((b) => b.textContent?.trim().toLowerCase().startsWith(e.key.toLowerCase()))
                  ?.focus();
              }
            }}
          >
            {options.map((o, i) => (
              <button
                type="button"
                role="option"
                aria-selected={o.value === selected}
                disabled={o.disabled}
                tabIndex={-1}
                key={o.value + '-' + i}
                onClick={() => choose(o)}
              >
                <span>{o.label}</span>
                {o.value === selected && <Check size={16} />}
              </button>
            ))}
          </div>,
          trigger.current?.closest('dialog') || document.body,
        )}
    </>
  );
}
